/**
 * Base User Validation Schemas
 * Schemas WITHOUT refinements - can be extended
 * Used as building blocks for frontend/backend validation
 */

import { z } from 'zod/v4'
import {
  emailValidation,
  passwordValidation,
  nameValidation,
  phoneValidation,
  uuidValidation,
  timezoneValidation,
  languageValidation,
  currencyValidation,
  themeValidation,
  dateFormatValidation,
  numberFormatValidation,
  roleValidation,
  userStatusValidation,
  optionalTextFieldValidation,
  booleanWithDefault,
} from '../shared/common.js'

// Base user schema without refinements - firstName/lastName now optional for registration
export const userBaseSchema = z.object({
  email: emailValidation,
  firstName: nameValidation.optional(),
  lastName: nameValidation.optional(),
})

// Extended user fields schema
export const userExtendedSchema = z.object({
  phone: phoneValidation.optional(),
  avatar: optionalTextFieldValidation(500),
  company: optionalTextFieldValidation(100),
  timezone: timezoneValidation.optional(),
  bio: optionalTextFieldValidation(500),
})

// Password schema for auth operations
export const passwordSchema = z.object({
  password: passwordValidation,
})

// Confirm password schema
export const confirmPasswordSchema = z.object({
  password: passwordValidation,
  confirmPassword: z.string(),
})

// User preferences base schema
export const userPreferencesBaseSchema = z.object({
  theme: themeValidation.default('system'),
  currency: currencyValidation.default('USD'),
  timezone: timezoneValidation.default('UTC'),
  language: languageValidation.default('en'),
  dateFormat: dateFormatValidation.default('YYYY-MM-DD'),
  numberFormat: numberFormatValidation.default('americano-comas'),
  compactMode: booleanWithDefault(false),
  autoSaveEnabled: booleanWithDefault(true),
})

// Notifications preferences schema
export const notificationsPreferencesSchema = z.object({
  email: booleanWithDefault(true),
  push: booleanWithDefault(true),
  desktop: booleanWithDefault(false),
  securityAlerts: booleanWithDefault(true),
  systemUpdates: booleanWithDefault(true),
  marketing: booleanWithDefault(false),
})

// Complete user preferences schema
export const userPreferencesSchema = userPreferencesBaseSchema.extend({
  notifications: notificationsPreferencesSchema,
})

// System fields schema (used internally)
export const systemFieldsSchema = z.object({
  id: uuidValidation,
  tenantId: uuidValidation,
  role: roleValidation,
  status: userStatusValidation,
  emailVerified: z.boolean(),
  createdBy: uuidValidation,
  lastModifiedBy: uuidValidation,
})

// Auth fields schema - WARNING: Contains sensitive data
// SECURITY: These schemas should only be used by backend packages
const authFieldsSchema = z.object({
  passwordHash: z.string().min(1), // SENSITIVE: backend only
  failedLoginAttempts: z.number().int().min(0).nullable().optional(),
  lastLoginAt: z.date().nullable().optional(),
  lockedUntil: z.date().nullable().optional(),
})

// Verification fields schema
export const verificationFieldsSchema = z.object({
  emailVerificationToken: z.string().nullable().optional(),
  emailVerificationExpiresAt: z.date().nullable().optional(),
  passwordResetToken: z.string().nullable().optional(),
  passwordResetExpiresAt: z.date().nullable().optional(),
})

// Audit fields schema
export const auditFieldsSchema = z.object({
  createdAt: z.date(),
  updatedAt: z.date(),
})

// Complete domain user schema (for internal use)
export const userDomainSchema = userBaseSchema
  .merge(userExtendedSchema)
  .merge(systemFieldsSchema)
  .merge(authFieldsSchema)
  .merge(verificationFieldsSchema)
  .merge(auditFieldsSchema)
  .extend({
    preferences: userPreferencesSchema.optional(),
  })

// Session base schema
export const sessionBaseSchema = z.object({
  id: uuidValidation,
  tenantId: uuidValidation,
  userId: uuidValidation,
  refreshToken: z.string().min(1),
  expiresAt: z.date(),
  isActive: z.boolean(),
})

// Session extended schema
export const sessionExtendedSchema = sessionBaseSchema.extend({
  accessTokenHash: z.string().optional(),
  userAgent: z.string().optional(),
  ipAddress: z
    .string()
    .regex(
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/,
    )
    .optional(),
  lastUsedAt: z.date(),
  revokedAt: z.date().nullable().optional(),
  revokedReason: z.string().optional(),
})

// JWT payload schema
export const jwtPayloadSchema = z.object({
  sub: uuidValidation, // user ID
  email: emailValidation,
  tenantId: uuidValidation,
  role: roleValidation,
  iat: z.number().int().positive(),
  exp: z.number().int().positive(),
})

// Request context schema
export const requestContextSchema = z.object({
  userId: uuidValidation,
  tenantId: uuidValidation,
  userAgent: z.string().optional(),
  ipAddress: z
    .string()
    .regex(
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/,
    )
    .optional(),
})
